import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRouteMatch } from 'react-router';
import * as yup from 'yup';
import { FormFieldType, IFormField } from '../../../../../extViewmodels';

import { toast } from 'sonner';
import {
	useClientCreateCustomSurveyResponseMutation,
	useClientGetCustomSurveyResponseByToken,
	useClientUpdateCustomSurveyResponseMutation,
} from '../../../../../queries';
import { Button } from '../../../../../web/components/Button';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { LoadingLogo } from '../../../../../web/components/LoadingLogo';
import { ISelectBoxOption, SelectBox } from '../../../../../web/components/SelectBox';
import { TextInput } from '../../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ICustomSurvey, ICustomSurveyResponse, IOperationResultNoValue } from '../../../../models/Api';
import { styleSheet } from './styles';

type Props = {
	survey: ICustomSurvey;
	onResponseSubmitted?(response: ICustomSurveyResponse): void;
	surveyResponse: ICustomSurveyResponse;
	setSurveyResponse: React.Dispatch<React.SetStateAction<ICustomSurveyResponse>>;
};

interface IFormValues {
	email?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	customProperties: Record<string, string>;
}

const getDefaultValues = (survey: ICustomSurvey): IFormValues => {
	const customProperties: Record<string, string> = {};
	survey.customForm?.fields.forEach(field => {
		customProperties[field.id] = '';
	});
	return {
		email: '',
		firstName: '',
		lastName: '',
		phoneNumber: '',
		customProperties,
	};
};

export const ErrorMessage = ({ message }: { message: string }) => (
	<p className={`${css(styleSheet.errorMessage)} ${css(styleSheet.noMargin)}`}>{message}</p>
);

// Helper function to build custom properties schema
const buildCustomPropertiesSchema = (fields: IFormField<string>[]) => {
	const schema: Record<string, yup.AnySchema> = {};

	fields.forEach(field => {
		if (!field.isHidden) {
			if (field.isOptional) {
				schema[field.id] = yup.string().optional();
			} else {
				schema[field.id] = yup.string().required(`${field.label} is required`);
			}
		}
	});

	return yup.object().shape({
		...schema,
	});
};

// US phone number regex
const usPhoneRegExp = new RegExp(/^((\+1)?[\s-]?)?\(?[1-9]\d\d\)?[\s-]?[1-9]\d\d[\s-]?\d\d\d\d$/);

export const CustomSurveyResponseForm = ({ survey, onResponseSubmitted, setSurveyResponse, surveyResponse }: Props) => {
	const { params: urlParams = {} } = useRouteMatch<{
		token?: string;
	}>();

	const { data: recordedResponse } = useClientGetCustomSurveyResponseByToken({
		accessToken: urlParams.token,
		retry: false,
	});

	const [loading, setLoading] = React.useState<boolean>(true);
	// Build validation schema based on survey configuration
	const validationSchema = React.useMemo(() => {
		const options = survey.options || {};
		return yup.object().shape({
			email:
				options.collectEmail && options.requireEmail
					? yup.string().trim().email('Invalid email').required('Email is required')
					: yup.string(),
			firstName:
				options.collectName && options.requireName ? yup.string().required('First name is required') : yup.string(),
			lastName:
				options.collectName && options.requireName ? yup.string().required('Last name is required') : yup.string(),
			phoneNumber:
				options.collectPhoneNumber && options.requirePhoneNumber
					? yup.string().trim().matches(usPhoneRegExp, 'Must be a valid phone number').required('Phone is required')
					: yup
							.string()
							.optional()
							.trim()
							.matches(usPhoneRegExp, { excludeEmptyString: true, message: 'Must be a valid phone number' }),
			customProperties: survey?.customForm?.fields
				? buildCustomPropertiesSchema(
						survey.customForm.fields.map(field => ({
							...field,
							id: field.id || '', // Ensure 'id' is defined, provide default if necessary
						}))
					)
				: yup.object().shape({}),
		});
	}, [survey]);

	const { handleSubmit, control, formState, getValues, setValue, trigger } = useForm({
		defaultValues: getDefaultValues(survey),
		resolver: yupResolver(validationSchema),
	});

	// Handle mutation success
	const handleMutationSuccess = (data: ICustomSurveyResponse) => {
		toast.success('Survey response successfully submitted');
		setSurveyResponse(data);
		if (onResponseSubmitted) {
			onResponseSubmitted(data);
		}
	};
	const introMessage = React.useMemo(() => {
		const text = survey.options?.intro?.document;
		if (!text) {
			return null;
		}
		// remove lev-content tag
		const textWithoutLevContent = text.replace(/<lev-content[^>]*>/g, '').replace(/<\/lev-content>/g, '');
		// remove all p tag styles
		const textWithoutPStyles = textWithoutLevContent.replace(/<p[^>]*>/g, '<p>').replace(/<\/p>/g, '</p>');
		return <div dangerouslySetInnerHTML={{ __html: textWithoutPStyles }} />;
	}, [survey]);

	// Handle mutation error
	const handleMutationError = (error: IOperationResultNoValue) => {
		toast.error(error?.systemMessage || 'Error submitting survey resposne');
		console.error('Mutation error:', error);
	};

	// Real mutations instead of mock ones
	const createResponseMutation = useClientCreateCustomSurveyResponseMutation({
		onSuccess: handleMutationSuccess,
		onError: handleMutationError,
	});

	const updateResponseMutation = useClientUpdateCustomSurveyResponseMutation({
		onSuccess: handleMutationSuccess,
		onError: handleMutationError,
	});

	// Load existing response data if available
	const isMountedRef = React.useRef(true);

	React.useEffect(() => {
		isMountedRef.current = true;
		setLoading(true);

		const initializeForm = () => {
			// Check if component is still mounted before updating state
			if (!isMountedRef.current) return;

			// First try using the directly provided surveyResponse prop
			if (surveyResponse?.id) {
				// Populate form with existing response data
				setValue('email', surveyResponse.contactInfo?.email || '');
				setValue('firstName', surveyResponse.contactInfo?.firstName || '');
				setValue('lastName', surveyResponse.contactInfo?.lastName || '');
				setValue('phoneNumber', surveyResponse.contactInfo?.phoneNumber || '');

				// Set custom properties
				const customProperties = surveyResponse?.customProperties || {};
				if (customProperties) {
					Object.entries(customProperties).forEach(([key, value]) => {
						setValue(`customProperties.${key}`, value || '');
					});
				}
			}
			// Then try the fetched recordedResponse if available
			else if (recordedResponse) {
				// Populate form with existing response data
				setValue('email', recordedResponse.contactInfo?.email || '');
				setValue('firstName', recordedResponse.contactInfo?.firstName || '');
				setValue('lastName', recordedResponse.contactInfo?.lastName || '');
				setValue('phoneNumber', recordedResponse.contactInfo?.phoneNumber || '');

				// Set custom properties
				const customProperties = recordedResponse?.customProperties || {};
				if (customProperties) {
					Object.entries(customProperties).forEach(([key, value]) => {
						setValue(`customProperties.${key}`, value || '');
					});
				}
				setSurveyResponse(recordedResponse);
			}

			setLoading(false);
		};

		// Short timeout to ensure form is ready for values
		const timeoutId = setTimeout(initializeForm, 300);

		return () => {
			isMountedRef.current = false;
			clearTimeout(timeoutId);
		};
	}, [recordedResponse, surveyResponse, setValue, setSurveyResponse]);

	const handleSave = async () => {
		const isValid = await trigger();
		if (!isValid) {
			return;
		}
		const values = getValues();

		const body: ICustomSurveyResponse = {
			contactInfo: {
				email: values.email?.trim(),
				firstName: values.firstName?.trim(),
				lastName: values.lastName?.trim(),
				phoneNumber: values.phoneNumber?.trim(),
			},
			customProperties: values.customProperties,
		};

		if (Object.values(values.customProperties).length) {
			const fieldsWithMissingInput = survey.customForm?.fields?.filter(
				x => !x.isOptional && !x.isHidden && !values.customProperties[x.id]
			);
			if (fieldsWithMissingInput?.length) {
				toast.error(`Please fill out all required fields`);
				return;
			}
		}

		// PUT/Update if we have an id
		if (surveyResponse?.id) {
			const payload = {
				...surveyResponse,
				...body,
			};
			updateResponseMutation.mutate({
				accessToken: urlParams.token,
				body: payload,
			});
			return;
		}

		// POST/Create
		createResponseMutation.mutate({ token: urlParams.token, body });
	};

	if (loading || !survey) {
		return (
			<div className={css(styleSheet.loadingLogo)}>
				<LoadingLogo size={200} />
			</div>
		);
	}

	return (
		<form className={css(styleSheet.surveyForm)} onSubmit={handleSubmit(handleSave)}>
			{introMessage ?? null}
			<div className={css(styleSheet.formContainer)}>
				{survey.options?.collectName ? (
					<>
						<div className={css(styleSheet.formControl)}>
							<label htmlFor='client-firstName' className={css(styleSheet.label)}>
								First Name {survey.options.requireName ? <span className={css(baseStyleSheet.required)}>*</span> : null}
							</label>
							<Controller
								name='firstName'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<TextInput
										inputId='client-firstName'
										name='firstName'
										type='text'
										aria-label='First Name'
										{...fieldProps}
									/>
								)}
							/>
							{formState.isSubmitted && formState.errors.firstName && (
								<ErrorMessage message={formState.errors.firstName.message} />
							)}
						</div>

						<div className={css(styleSheet.formControl)}>
							<label htmlFor='client-lastName' className={css(styleSheet.label)}>
								Last Name {survey.options.requireName ? <span className={css(baseStyleSheet.required)}>*</span> : null}
							</label>
							<Controller
								name='lastName'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<TextInput inputId='client-lastName' name='lastName' type='text' {...fieldProps} />
								)}
							/>
							{formState.isSubmitted && formState.errors.lastName && (
								<ErrorMessage message={formState.errors.lastName.message} />
							)}
						</div>
					</>
				) : null}

				{survey.options?.collectEmail ? (
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-email' className={css(styleSheet.label)}>
							Email {survey?.options.requireEmail ? <span className={css(baseStyleSheet.required)}>*</span> : null}
						</label>
						<Controller
							name='email'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<TextInput inputId='client-email' type='text' {...fieldProps} />
							)}
						/>
						{formState.isSubmitted && formState.errors.email && (
							<ErrorMessage message={formState.errors.email.message} />
						)}
					</div>
				) : null}
				{survey.options?.collectPhoneNumber ? (
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-phone' className={css(styleSheet.label)}>
							Phone{' '}
							{!survey?.options.requirePhoneNumber ? (
								<small style={{ fontSize: 11 }}>(Optional)</small>
							) : (
								<span className={css(baseStyleSheet.required)}>*</span>
							)}
						</label>
						<Controller
							name='phoneNumber'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<TextInput inputId='client-phone' type='text' {...fieldProps} placeholder='(999)999-9999' />
							)}
						/>
						{formState.isSubmitted && formState.errors.phoneNumber && (
							<ErrorMessage message={formState.errors.phoneNumber.message} />
						)}
					</div>
				) : null}

				{survey?.customForm?.fields
					?.filter(f => !f.isHidden)
					.map(f => {
						const fieldDropdownOptions = f.options?.map(o => {
							return {
								id: o.id,
								title: o.label,
								value: o.label,
							};
						});

						return (
							<div key={f.id} className={css(styleSheet.formControlFullWidth)}>
								<label htmlFor={f.id} className={css(styleSheet.label)}>
									{f.label} {!f.isOptional ? <span className={css(baseStyleSheet.required)}>*</span> : null}
								</label>
								<Controller
									name={`customProperties.${f.id}`}
									control={control}
									render={({ field: { ref, ...fieldProps } }) => {
										if (f.fieldType === FormFieldType.String) {
											return (
												<>
													<TextInput
														type='text'
														inputId={f.id}
														inputClassName={css(styleSheet.textInput)}
														value={fieldProps.value || ''}
														{...fieldProps}
													/>
													{formState?.errors.customProperties?.[f.id] && (
														<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
													)}
												</>
											);
										}

										if (f.fieldType === FormFieldType.Option) {
											const onSelectionChanged = (selectedOption: ISelectBoxOption<string>) => {
												setValue(`customProperties.${f.id}`, selectedOption.id);
											};
											const onRenderTriggerTitle = (selectedOption: ISelectBoxOption<string>) => {
												return <span>{selectedOption.title}</span>;
											};
											return (
												<>
													<SelectBox
														options={fieldDropdownOptions}
														onRenderSelectedOptionTitle={onRenderTriggerTitle}
														onSelectionChanged={onSelectionChanged}
														placeholder={<span>Select option</span>}
														triggerClassName={css(styleSheet.selectBoxTriggerButton)}
														{...fieldProps}
														selectedOption={fieldDropdownOptions?.find(x => x.id === fieldProps.value) || undefined}
													/>
													{formState?.errors.customProperties?.[f.id] && (
														<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
													)}
												</>
											);
										}

										if (f.fieldType === FormFieldType.MultipleOptions) {
											const fieldCheckboxOptions = f.options?.map(o => {
												const fieldCheckboxSelectedOptions: string[] = fieldProps?.value?.split(',') ?? [];
												/**
												 * Method to update the value of the field
												 * @param e {React.ChangeEvent<HTMLInputElement>}
												 * sets a {String} value of the field
												 */
												const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
													const newOptions = [...fieldCheckboxSelectedOptions];
													if (e.target.checked) {
														newOptions.push(o.id);
													} else {
														newOptions.splice(fieldCheckboxSelectedOptions.indexOf(o.id), 1);
													}

													setValue(`customProperties.${f.id}`, `${newOptions.join(',')}`);
												};
												/**
												 * Use the onChange method to update the value of the field
												 */
												fieldProps.onChange = onChange;
												return (
													<Checkbox
														key={o.id}
														id={o.id}
														className={css(styleSheet.checkbox)}
														checked={fieldCheckboxSelectedOptions?.includes(o.id)}
														{...fieldProps}
													>
														<span>{o.label}</span>
													</Checkbox>
												);
											});
											return (
												<>
													{fieldCheckboxOptions}
													{formState?.errors.customProperties?.[f.id] && (
														<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
													)}
												</>
											);
										}

										return null;
									}}
								/>
							</div>
						);
					})}
			</div>

			<footer className={css(styleSheet.actions)}>
				<div className={css(styleSheet.divider)} />
				<div className={css(styleSheet.buttonContainer)}>
					<Button
						type='submit'
						kind='primary'
						disabled={createResponseMutation.isLoading || updateResponseMutation.isLoading}
						isLoading={createResponseMutation.isLoading || updateResponseMutation.isLoading}
						label={surveyResponse?.id ? 'Update Response' : 'Submit Survey'}
					/>
				</div>
			</footer>
		</form>
	);
};
